import axios, { AxiosResponse } from 'axios';
import { apiClient } from '../apiClient';
import { LoadNature } from '../materials/loadNatures';
import { TruckType } from '../materials/trucksTypes';
import { TruckCompany } from '../materials/trucksCompanies';
import { Quarry } from '../sites/quarries';
import { DailyTransfer } from './dailyTransfers';
import { Site } from '../sites/sites';
import { Truck } from '../materials/trucks';
import { Dump } from '../materials/dumps';

export interface DailyTruckPlanned {
    _id: string;
    activityId: string;
    truckTypeId: TruckType;
    trucksAmount: number;
    ridesAmount?: number;
    truckCompanyId?: TruckCompany;
    areTrucksRented?: boolean;
    mode?: number;
    transfers: DailyTransfer[];
    departureLoadNatureId?: LoadNature;
    arrivalLoadNatureId?: LoadNature;
    departureSiteId?: Site;
    arrivalSiteId?: Site;
    departureQuarryId?: Quarry;
    arrivalQuarryId?: Quarry;
    departureDumpId?: Dump;
    arrivalDumpId?: Dump;
    completedAmount: number;
    truckId?: Truck;
    observations?: string;
    rank?: number;
    initialTrucksAmount?: number;
    parentPlannedId?: string;
}

export interface DailyTrucksPlannedResponse {
    message: string;
    data: DailyTruckPlanned[];
}

export interface CreateDailyTruckPlannedBody {
    activityId: string;
    truckTypeId: string;
    trucksAmount: number;
    initialTrucksAmount?: number | null;
    ridesAmount?: number;
    truckCompanyId?: string;
    areTrucksRented?: boolean;
    mode?: number;
    departureLoadNatureId?: string | null;
    arrivalLoadNatureId?: string | null;
    departureSiteId?: string | null;
    arrivalSiteId?: string | null;
    departureQuarryId?: string | null;
    arrivalQuarryId?: string | null;
    departureDumpId?: string | null;
    arrivalDumpId?: string | null;
    truckId?: string | null;
    completedAmount?: number;
    observations?: string;
    rank?: number;
}

export interface DailyTruckPlannedResponse {
    message: string;
    data: DailyTruckPlanned;
}

export const createDailyTruckPlanned = async (body: CreateDailyTruckPlannedBody): Promise<DailyTruckPlannedResponse> => {
    try {
        const response: AxiosResponse<DailyTruckPlannedResponse> = await apiClient.post('/daily-trucksplanned', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface AssignDailyTruckPlannedBody {
    userIds: string[];
    dailyTruckPlannedId: string;
}

export const assignDailyTruckPlanned = async (body: AssignDailyTruckPlannedBody): Promise<DailyTrucksPlannedResponse> => {
    try {
        const response: AxiosResponse<DailyTrucksPlannedResponse> = await apiClient.post('/daily-trucksplanned/assign', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

// UPDATE Daily Truck Planned
export interface UpdateDailyTruckPlannedBody {
    activityId?: string;
    truckTypeId?: string;
    trucksAmount?: number;
    initialTrucksAmount?: number | null;
    ridesAmount?: number;
    truckCompanyId?: string;
    areTrucksRented?: boolean;
    mode?: number;
    departureLoadNatureId?: string | null;
    arrivalLoadNatureId?: string | null;
    departureSiteId?: string | null;
    arrivalSiteId?: string | null;
    departureQuarryId?: string | null;
    arrivalQuarryId?: string | null;
    departureDumpId?: string | null;
    arrivalDumpId?: string | null;
    truckId?: string | null;
    completedAmount?: number;
    observations?: string;
    rank?: number;
}

export interface UpdateDailyTruckPlannedQueries {
    autoTruck?: boolean;
}

export const updateDailyTruckPlanned = async (
    id: string,
    body: UpdateDailyTruckPlannedBody,
    queries?: UpdateDailyTruckPlannedQueries
): Promise<DailyTruckPlannedResponse> => {
    try {
        const response: AxiosResponse<DailyTruckPlannedResponse> = await apiClient.patch('/daily-trucksplanned/' + id, body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateDailyTruckRankBody {
    targetItemId: string;
}

export const updateDailyTruckPlannedRank = async (id: string, body: UpdateDailyTruckRankBody): Promise<DailyTruckPlannedResponse> => {
    try {
        const response: AxiosResponse<DailyTruckPlannedResponse> = await apiClient.patch('/daily-trucksplanned/' + id + '/rank/', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateDailyTruckRankUpDownBody {
    action: 'up' | 'down';
}

export const updateDailyTruckPlannedRankUpDown = async (id: string, body: UpdateDailyTruckRankUpDownBody): Promise<DailyTruckPlannedResponse> => {
    try {
        const response: AxiosResponse<DailyTruckPlannedResponse> = await apiClient.patch('/daily-trucksplanned/' + id + '/rank/up-down', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

// DELETE Daily Truck Planned
export interface DeleteDailyTruckPlannedResponse {
    message: string;
    data: DailyTruckPlanned;
}

export const deleteDailyTruckPlanned = async (id: string): Promise<DeleteDailyTruckPlannedResponse> => {
    try {
        const response: AxiosResponse<DeleteDailyTruckPlannedResponse> = await apiClient.delete(`/daily-trucksplanned/${id}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
